import { useTranslation } from 'react-i18next';
import { ChevronRight, Pencil, Trash2 } from 'lucide-react';
import type { EntityStatus } from '@nbh/shared';
import { locationStatus, type LocationNode } from '../../lib/adminApi';
import { EntityStatusBadge } from '../ui/EntityStatusBadge';

/**
 * One place in the tree, and the controls that act on it.
 *
 * Split out of `SuperAdminRooms` on the 400-line rule, but the seam is a real one: this
 * file is what a single row looks like and what may be done to it, and the tab beside it
 * is the tree, the open/closed state and the confirmations. Nothing here fetches.
 */
export function PlaceRow({
  node,
  depth,
  selected,
  onSelect,
  onRetire,
  onDelete,
  onEdit,
  expanded,
  onToggle,
  onReinstate,
  onToggleMaintenance,
}: {
  node: LocationNode;
  depth: number;
  selected: boolean;
  onSelect: () => void;
  onRetire: () => void;
  onDelete: () => void;
  onEdit: () => void;
  expanded: boolean;
  onToggle: () => void;
  onReinstate: () => void;
  onToggleMaintenance: () => void;
}) {
  const { t } = useTranslation();
  const status = locationStatus(node);
  const childCount = node.children.length;

  return (
    <li
      // Wraps, because three controls plus a name and a QR token do not fit beside each
      // other on a 390px screen. Unwrapped they pushed the row wider than the viewport
      // and left the delete button off the side of the page entirely — reachable only by
      // scrolling sideways, which is not a thing anyone does one-handed in a plant room.
      className={`flex flex-wrap items-center gap-x-2 border-b border-slate-800 px-3 py-1 last:border-b-0 ${
        selected ? 'bg-brookfields-green-dark/60' : ''
      }`}
      style={{ paddingLeft: `${12 + depth * 18}px` }}
    >
      <span className="flex basis-full items-center gap-1 sm:flex-1 sm:basis-auto">
        {/*
          Its own control, not part of the name button: opening a floor and choosing
          where the next room goes are different intentions, and one tap cannot mean
          both. Rendered as a fixed-width spacer when there is nothing to open, so the
          names in a list still line up.
        */}
        {childCount > 0 ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={t(expanded ? 'superAdmin.rooms.collapse' : 'superAdmin.rooms.expand', {
              name: node.name,
              count: childCount,
            })}
            className="flex min-h-[48px] w-8 shrink-0 items-center justify-center bg-transparent text-slate-400"
          >
            <ChevronRight
              className={`size-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
              aria-hidden="true"
            />
          </button>
        ) : (
          <span className="w-8 shrink-0" aria-hidden="true" />
        )}

        <button
          type="button"
          // Selecting sets where the next place is added: the usual next action after
          // looking at a floor is putting a room inside it.
          onClick={onSelect}
          aria-pressed={selected}
          className="flex min-h-[48px] min-w-0 flex-1 items-center gap-2 bg-transparent text-left text-sm"
        >
          <span className={`truncate ${node.isActive ? 'text-slate-100' : 'text-slate-600'}`}>
            {node.name}
          </span>
          {node.qrCode && <span className="truncate text-xs text-sky-400">· {node.qrCode}</span>}
          <EntityStatusBadge status={status} />
          {/* Collapsed rows would otherwise give no hint that anything is inside. */}
          {childCount > 0 && !expanded && (
            <span className="shrink-0 text-xs text-slate-500">
              {t('superAdmin.rooms.childCount', { count: childCount })}
            </span>
          )}
        </button>
      </span>

      <PlaceActions
        node={node}
        status={status}
        onEdit={onEdit}
        onRetire={onRetire}
        onDelete={onDelete}
        onReinstate={onReinstate}
        onToggleMaintenance={onToggleMaintenance}
      />
    </li>
  );
}

/**
 * The per-place controls, split out only to keep `PlaceRow` inside the function-length
 * rule. Ordering is the meaningful part and is unchanged: correct, then the reversible
 * state changes, then — behind a divider — the one that cannot be undone.
 */
function PlaceActions({
  node,
  status,
  onEdit,
  onRetire,
  onDelete,
  onReinstate,
  onToggleMaintenance,
}: {
  node: LocationNode;
  status: EntityStatus;
  onEdit: () => void;
  onRetire: () => void;
  onDelete: () => void;
  onReinstate: () => void;
  onToggleMaintenance: () => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      {/* First and non-destructive: fixing a typo is the act that should be easiest
          to reach, and the one that keeps people away from the other two. */}
      <button
        type="button"
        onClick={onEdit}
        aria-label={t('superAdmin.rooms.edit', { name: node.name })}
        title={t('superAdmin.rooms.edit', { name: node.name })}
        className="min-h-[48px] shrink-0 rounded bg-transparent px-2 text-slate-300"
      >
        <Pencil className="size-4" aria-hidden="true" />
      </button>

      {status === 'RETIRED' ? (
        <button
          type="button"
          onClick={onReinstate}
          className="min-h-[48px] shrink-0 rounded bg-transparent px-3 text-xs text-emerald-300"
        >
          {t('superAdmin.rooms.reinstate')}
        </button>
      ) : (
        <>
          <button
            type="button"
            onClick={onToggleMaintenance}
            className="min-h-[48px] shrink-0 rounded bg-transparent px-2 text-xs text-amber-300"
          >
            {status === 'MAINTENANCE'
              ? t('superAdmin.rooms.endMaintenance')
              : t('superAdmin.rooms.startMaintenance')}
          </button>
          <button
            type="button"
            onClick={onRetire}
            className="min-h-[48px] shrink-0 rounded bg-transparent px-2 text-xs text-red-300"
          >
            {t('superAdmin.rooms.retire')}
          </button>
        </>
      )}

      {/*
        Deliberately last, icon-only and behind a divider. Delete is irreversible and
        Retire is not, and the two must not read as a pair of equivalent choices sitting
        side by side under a thumb.
      */}
      <button
        type="button"
        onClick={onDelete}
        aria-label={t('superAdmin.rooms.delete', { name: node.name })}
        title={t('superAdmin.rooms.delete', { name: node.name })}
        className="ml-1 min-h-[48px] shrink-0 border-l border-slate-800 bg-transparent px-3 text-red-500"
      >
        <Trash2 className="size-4" aria-hidden="true" />
      </button>
    </>
  );
}
